/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';

import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IAccountInvoiceTileData } from './account-invoice-tile.data';
import { IAccountInvoiceTileProps } from './account-invoice-tile.props.autogenerated';

import {AccountInvoiceTileDescription} from './components';
import LinksComponent from './components/account-invoice-tile-links';

export interface IAccountInvoiceTileViewProps extends IAccountInvoiceTileProps<IAccountInvoiceTileData> {
    AccountInvoiceTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;

}

/**
 *
 * AccountInvoiceTile component
 * @extends {React.PureComponent<IAccountInvoiceTileProps<IAccountInvoiceTileData>>}
 */
class AccountInvoiceTile extends React.PureComponent<IAccountInvoiceTileProps<IAccountInvoiceTileData>> {
    private telemetryContent: ITelemetryContent;
    constructor(props: IAccountInvoiceTileProps<IAccountInvoiceTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
    }

    public render(): JSX.Element {
        const {
            config,
            data,
            resources,
            context: {
                cultureFormatter: {
                    formatCurrency,
                    // @ts-ignore
                    currencyCode
                }
            }
        } = this.props;
        const { className, heading, links } = config;
        const { accountInvoiceTileDescription } = resources;
        let openinvoiceAmount = 0;
        let invoiceTotalAmount:string|undefined;

        if (data && data.invoices && data.invoices.result) {
            data.invoices.result.forEach(invoiceItem => {
                if (invoiceItem.AmountBalance) {
                    openinvoiceAmount += invoiceItem.AmountBalance;
                }
            }
            );
            invoiceTotalAmount = formatCurrency(openinvoiceAmount.toFixed(2), currencyCode);
        }
        const description = invoiceTotalAmount && format(accountInvoiceTileDescription, invoiceTotalAmount);

        const viewProps = {
            ...this.props,
            className: className,
            AccountInvoiceTile: {
                moduleProps: this.props,
                className: classnames('ms-account-invoice-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-invoice-tile__heading'
                    tag={heading.tag || 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && <LinksComponent {...{ links: links, onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request, telemetryContent: this.telemetryContent }} />,
            description: description && <AccountInvoiceTileDescription description={description} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.heading.text = event.target.value;
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountInvoiceTile;
